// The `games` interface: the library's data. A provider (games-local over
// `storage`, a remote one later) implements `Games`; the library screen,
// the home card and every review panel read through it. Panels get their
// own folder inside a game from `folder`, and nothing else sees a path.
import type { Storage } from '@chess/api-storage';
import type { GameId, GameRecord, GameSummary } from '@chess/api-model';

export * from './types.ts';
export * from './schema.ts';
export * from './route.ts';

export const GAMES_API = 'games';
export const GAMES_VERSION = '1.0.0';

export type GamesEvent =
  | { type: 'added'; game: GameSummary }
  | { type: 'removed'; id: GameId };

import type { GameFilter } from '@chess/api-model';
export * from './filter.ts';

export interface ListOptions {
  /** Only this account's games. */
  owner?: string;
  /** Newest first by default. */
  order?: 'newest' | 'oldest';
  /** Skip this many games of the ordered list; 0 by default. */
  offset?: number;
  /** At most this many games from `offset` on; all of them by default. */
  size?: number;
  /** Only games matching this filter (outcome, colour, time class, opponent); see `./filter`. */
  filter?: GameFilter;
}

/** What `count` filters by: the same filter as `list`, without ordering or range. */
export type CountOptions = Pick<ListOptions, 'owner' | 'filter'>;

export interface Games {
  /** Summaries of the games in the library. */
  list(options?: ListOptions): Promise<GameSummary[]>;
  /** How many games `list` would return with the same filter and no range. */
  count(options?: CountOptions): Promise<number>;
  /** Every stored game's whole record played after `since` (ISO 8601; all of them without), unordered: the game store's one read. */
  records(options?: { since?: string }): Promise<GameRecord[]>;
  /** The whole record, or undefined if there is no such game. */
  get(id: GameId): Promise<GameRecord | undefined>;
  /** Stores a game. A record with an existing id replaces it. */
  put(record: GameRecord): Promise<void>;
  /** Removes a game and every plugin's folder inside it. */
  remove(id: GameId): Promise<void>;
  /**
   * The folder a plugin keeps inside a game, scoped so the plugin can write
   * nowhere else. The folder exists once something is written to it.
   */
  folder(id: GameId, plugin: string): Storage;
  /** Hears additions and removals. Returns the unsubscribe. */
  subscribe(listener: (event: GamesEvent) => void): () => void;
}
